import type { InstrumentState, InstrumentType, WamParameterInfo } from '@/domain/types';
import type { ParamDescriptor } from '../effects/descriptors';

/**
 * Instrument parameters, declared exactly like effect parameters so that the
 * inspector, the file parser's clamping and automation all read one definition.
 */

export const WAVEFORMS = [
  { value: 0, label: 'Sine' },
  { value: 1, label: 'Triangle' },
  { value: 2, label: 'Saw' },
  { value: 3, label: 'Square' },
] as const;

export interface InstrumentDescriptor {
  type: InstrumentType;
  label: string;
  summary: string;
  params: readonly ParamDescriptor[];
}

const SYNTH_PARAMS: readonly ParamDescriptor[] = [
  {
    id: 'wave',
    label: 'Wave',
    min: 0,
    max: 3,
    default: 2,
    unit: '',
    scale: 'linear',
    step: 1,
    choices: WAVEFORMS,
    // Switching oscillator type mid-note would restart the waveform; there is
    // no AudioParam to ramp.
    automatable: false,
  },
  { id: 'octave', label: 'Octave', min: -3, max: 3, default: 0, unit: '', scale: 'linear', step: 1, automatable: false },
  { id: 'detune', label: 'Detune', min: 0, max: 50, default: 8, unit: 'cents', scale: 'linear', automatable: false },
  { id: 'attack', label: 'Attack', min: 0.001, max: 3, default: 0.01, unit: 's', scale: 'log', automatable: false },
  { id: 'decay', label: 'Decay', min: 0.005, max: 3, default: 0.25, unit: 's', scale: 'log', automatable: false },
  { id: 'sustain', label: 'Sustain', min: 0, max: 1, default: 0.7, unit: '', scale: 'linear', automatable: false },
  { id: 'release', label: 'Release', min: 0.005, max: 6, default: 0.3, unit: 's', scale: 'log', automatable: false },
  {
    // Shared across the voices rather than set per note, because that is what
    // bending is: one hand on one wheel moving everything that is sounding.
    // Two octaves either way covers any bend range a file is likely to declare.
    id: 'bend',
    label: 'Bend',
    min: -24,
    max: 24,
    default: 0,
    unit: 'st',
    scale: 'linear',
  },
  { id: 'cutoff', label: 'Cutoff', min: 60, max: 18000, default: 4000, unit: 'Hz', scale: 'log' },
  { id: 'resonance', label: 'Resonance', min: 0.1, max: 20, default: 1.2, unit: 'Q', scale: 'log' },
  { id: 'filterEnv', label: 'Filter env', min: 0, max: 1, default: 0.35, unit: '', scale: 'linear', automatable: false },
  { id: 'volume', label: 'Volume', min: 0, max: 1.5, default: 0.7, unit: '', scale: 'linear' },
  {
    // The wheel on the left of every keyboard. Opens the filter by up to three
    // octaves above the cutoff, live from a controller or from a lane.
    id: 'modWheel',
    label: 'Mod wheel',
    min: 0,
    max: 1,
    default: 0,
    unit: '',
    scale: 'linear',
  },
];

/**
 * The drum kit. Pads are addressed by MIDI note number so a drum pattern is an
 * ordinary MIDI clip — the same clip the piano roll edits — and the step
 * sequencer is a second view over that one representation rather than a
 * parallel data model that could drift out of sync with it.
 */
export interface DrumPad {
  /** MIDI note that triggers this pad (General MIDI positions). */
  pitch: number;
  id: string;
  label: string;
}

export const DRUM_PADS: readonly DrumPad[] = [
  { pitch: 36, id: 'kick', label: 'Kick' },
  { pitch: 38, id: 'snare', label: 'Snare' },
  { pitch: 39, id: 'clap', label: 'Clap' },
  { pitch: 42, id: 'hatClosed', label: 'Closed Hat' },
  { pitch: 46, id: 'hatOpen', label: 'Open Hat' },
  { pitch: 45, id: 'tom', label: 'Tom' },
  { pitch: 37, id: 'perc', label: 'Perc' },
];

export function padForPitch(pitch: number): DrumPad | undefined {
  return DRUM_PADS.find((pad) => pad.pitch === pitch);
}

const DRUM_PARAMS: readonly ParamDescriptor[] = [
  { id: 'volume', label: 'Volume', min: 0, max: 1.5, default: 0.9, unit: '', scale: 'linear' },
  { id: 'tone', label: 'Tone', min: 500, max: 18000, default: 12000, unit: 'Hz', scale: 'log' },
  ...DRUM_PADS.flatMap((pad): ParamDescriptor[] => [
    {
      id: `${pad.id}Level`,
      label: `${pad.label} level`,
      min: 0,
      max: 1.5,
      default: 0.85,
      unit: '',
      scale: 'linear',
      automatable: false,
    },
    {
      id: `${pad.id}Tune`,
      label: `${pad.label} tune`,
      min: -12,
      max: 12,
      default: 0,
      unit: 'st',
      scale: 'linear',
      automatable: false,
    },
    {
      id: `${pad.id}Decay`,
      label: `${pad.label} decay`,
      min: 0.02,
      max: 2,
      default: 0.3,
      unit: 's',
      scale: 'log',
      automatable: false,
    },
  ]),
];

/**
 * The sampler shares the synth's envelope and filter section — the same
 * controls, doing the same thing to a different sound source — and adds only
 * what a sampler needs that a synth does not.
 */
const SAMPLER_PARAMS: readonly ParamDescriptor[] = [
  { id: 'transpose', label: 'Transpose', min: -24, max: 24, default: 0, unit: 'st', scale: 'linear', step: 1, automatable: false },
  {
    // Shared across the voices, like the synth's. A sampler pitches by playback
    // rate, but a bend has to *multiply* that and a connection can only sum — so
    // this rides on `detune`, which is additive and in cents.
    id: 'bend',
    label: 'Bend',
    min: -24,
    max: 24,
    default: 0,
    unit: 'st',
    scale: 'linear',
  },
  { id: 'attack', label: 'Attack', min: 0.001, max: 3, default: 0.002, unit: 's', scale: 'log', automatable: false },
  { id: 'decay', label: 'Decay', min: 0.005, max: 6, default: 1, unit: 's', scale: 'log', automatable: false },
  { id: 'sustain', label: 'Sustain', min: 0, max: 1, default: 1, unit: '', scale: 'linear', automatable: false },
  { id: 'release', label: 'Release', min: 0.005, max: 6, default: 0.12, unit: 's', scale: 'log', automatable: false },
  { id: 'cutoff', label: 'Cutoff', min: 60, max: 18000, default: 18000, unit: 'Hz', scale: 'log' },
  { id: 'resonance', label: 'Resonance', min: 0.1, max: 20, default: 0.7, unit: 'Q', scale: 'log' },
  { id: 'filterEnv', label: 'Filter env', min: 0, max: 1, default: 0, unit: '', scale: 'linear', automatable: false },
  { id: 'volume', label: 'Volume', min: 0, max: 1.5, default: 0.9, unit: '', scale: 'linear' },
  {
    // The wheel on the left of every keyboard. Opens the filter by up to three
    // octaves above the cutoff, live from a controller or from a lane.
    id: 'modWheel',
    label: 'Mod wheel',
    min: 0,
    max: 1,
    default: 0,
    unit: '',
    scale: 'linear',
  },
  {
    // How much of the level is velocity's to decide. At one, a note struck at
    // 40 is a third as loud as one at 127; at zero every hit is the same,
    // which a kit often wants. The zones' velocity layers say which sample
    // answers; this says how loud.
    id: 'velocity',
    label: 'Velocity',
    min: 0,
    max: 1,
    default: 1,
    unit: '',
    scale: 'linear',
    automatable: false,
  },
];

export const INSTRUMENT_DESCRIPTORS: Record<InstrumentType, InstrumentDescriptor> = {
  synth: {
    type: 'synth',
    label: 'Synth',
    summary: 'Two-oscillator subtractive synthesiser',
    params: SYNTH_PARAMS,
  },
  drumMachine: {
    type: 'drumMachine',
    label: 'Drum Machine',
    summary: 'Seven synthesised percussion voices',
    params: DRUM_PARAMS,
  },
  sampler: {
    type: 'sampler',
    label: 'Sampler',
    summary: 'Plays your own audio across the keyboard',
    params: SAMPLER_PARAMS,
  },
  wam: {
    type: 'wam',
    label: 'Web Audio Module',
    summary: 'A plugin instrument loaded from a URL, in the open Web Audio Modules format',
    // The host's own controls. The plugin's parameters are whatever it
    // reported once it loaded; see `instrumentDescriptor`.
    params: [
      { id: 'volume', label: 'Volume', min: 0, max: 1.5, default: 0.9, unit: '', scale: 'linear' },
      // The wheels leave as the MIDI a keyboard sends, which is not a
      // parameter a lane can ramp; a lane on the plugin's own parameters can.
      { id: 'bend', label: 'Bend', min: -24, max: 24, default: 0, unit: 'st', scale: 'linear', automatable: false },
      { id: 'modWheel', label: 'Mod wheel', min: 0, max: 1, default: 0, unit: '', scale: 'linear', automatable: false },
    ],
  },
};

/**
 * The descriptor for an instrument as it is, not just its type: a hosted
 * plugin's parameters are whatever the plugin reported, kept in its state,
 * and its label is the plugin's name.
 */
export function instrumentDescriptor(instrument: InstrumentState): InstrumentDescriptor {
  if (instrument.type !== 'wam') return INSTRUMENT_DESCRIPTORS[instrument.type];
  const data = instrument.data;
  const reported = Array.isArray(data.wamParameters) ? (data.wamParameters as WamParameterInfo[]) : [];
  const own = new Set(INSTRUMENT_DESCRIPTORS.wam.params.map((p) => p.id));
  return {
    type: 'wam',
    label: typeof data.wamName === 'string' ? data.wamName : 'Web Audio Module',
    summary: INSTRUMENT_DESCRIPTORS.wam.summary,
    params: [
      ...INSTRUMENT_DESCRIPTORS.wam.params,
      ...reported
        .filter((p) => !own.has(p.id))
        .map((p) => ({ id: p.id, label: p.label, min: p.min, max: p.max, default: p.default, unit: p.units, scale: 'linear' as const })),
    ],
  };
}

export function instrumentParamDescriptorFor(instrument: InstrumentState, paramId: string): ParamDescriptor | undefined {
  return instrumentDescriptor(instrument).params.find((p) => p.id === paramId);
}

export function defaultInstrumentParams(type: InstrumentType): Record<string, number> {
  const out: Record<string, number> = {};
  for (const param of INSTRUMENT_DESCRIPTORS[type].params) out[param.id] = param.default;
  return out;
}

export function instrumentParamDescriptor(type: InstrumentType, paramId: string): ParamDescriptor | undefined {
  return INSTRUMENT_DESCRIPTORS[type].params.find((p) => p.id === paramId);
}

/** Equal-tempered frequency for a MIDI note number. */
export function midiToFrequency(pitch: number, detuneSemitones = 0): number {
  return 440 * Math.pow(2, (pitch + detuneSemitones - 69) / 12);
}

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/** `C4`, `F#2` — the naming a musician expects, with middle C as C4. */
export function midiNoteName(pitch: number): string {
  const clamped = Math.max(0, Math.min(127, Math.round(pitch)));
  return `${NOTE_NAMES[clamped % 12]}${Math.floor(clamped / 12) - 1}`;
}

export function isBlackKey(pitch: number): boolean {
  return [1, 3, 6, 8, 10].includes(((pitch % 12) + 12) % 12);
}
